import { useState } from 'react'
import { Comment, User } from '@/types'
import { useFileLinksTarget } from '../hooks/useFileLinksTarget'
import OfficialFroalaEditor from './OfficialFroalaEditor'
import {
  UserCircleIcon,
  PaperAirplaneIcon,
  EllipsisVerticalIcon,
  PencilIcon,
  TrashIcon
} from '@heroicons/react/24/outline'
import { Menu, Transition } from '@headlessui/react'
import { Fragment } from 'react'


interface CommentSectionProps {
  comments: Comment[]
  issueId: string
  currentUser: User
  onAddComment: (content: string) => Promise<void>
  onUpdateComment: (commentId: string, content: string) => Promise<void>
  onDeleteComment: (commentId: string) => Promise<void>
  canCreateComment?: boolean
  canEditComment?: boolean
  canDeleteComment?: boolean
}

interface CommentItemProps {
  comment: Comment
  currentUser: User
  onUpdate: (content: string) => Promise<void>
  onDelete: () => Promise<void>
  canEdit?: boolean
  canDelete?: boolean
}

const CommentItem = ({ comment, currentUser, onUpdate, onDelete, canEdit = true, canDelete = true }: CommentItemProps) => {
  const [isEditing, setIsEditing] = useState(false)
  const [editContent, setEditContent] = useState(comment.content)
  const [loading, setLoading] = useState(false)
  
  // 使用 Hook 來處理檔案連結
  const commentContentRef = useFileLinksTarget(comment.content)

  const handleSave = async () => {
    if (editContent.trim() === comment.content.trim()) {
      setIsEditing(false)
      return
    }

    setLoading(true)
    try {
      await onUpdate(editContent)
      setIsEditing(false)
    } catch (error) {
      console.error('更新留言失敗:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleCancel = () => {
    setEditContent(comment.content)
    setIsEditing(false)
  }

  const handleDelete = async () => {
    if (window.confirm('確定要刪除這個留言嗎？')) {
      setLoading(true)
      try {
        await onDelete()
      } catch (error) {
        console.error('刪除留言失敗:', error)
      } finally {
        setLoading(false)
      }
    }
  }

  const isOwner = comment.authorId === currentUser.id

  return (
    <div className="flex space-x-3 p-4">
      {/* 使用者頭像 */}
      <div className="flex-shrink-0">
        {comment.author.avatar ? (
          <img
            src={comment.author.avatar}
            alt={comment.author.name}
            className="w-8 h-8 rounded-full"
          />
        ) : (
          <div className="w-8 h-8 rounded-full bg-gray-300 flex items-center justify-center">
            <span className="text-xs font-medium text-gray-600">
              {comment.author.name.charAt(0).toUpperCase()}
            </span>
          </div>
        )}
      </div>

      {/* 留言內容 */}
      <div className="flex-1 min-w-0">
        {/* 留言標題 */}
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <span className="text-sm font-medium text-gray-900">
              {comment.author.name}
            </span>
            <span className="text-xs text-gray-500">
              {new Date(comment.createdAt).toLocaleString('zh-TW')}
            </span>
            {comment.updatedAt !== comment.createdAt && (
              <span className="text-xs text-gray-400">(已編輯)</span>
            )}
          </div>

          {/* 操作選單 */}
          {isOwner && (
            <Menu as="div" className="relative">
              <Menu.Button className="p-1 rounded hover:bg-gray-100">
                <EllipsisVerticalIcon className="w-4 h-4 text-gray-400" />
              </Menu.Button>
              <Transition
                as={Fragment}
                enter="transition ease-out duration-100"
                enterFrom="transform opacity-0 scale-95"
                enterTo="transform opacity-100 scale-100"
                leave="transition ease-in duration-75"
                leaveFrom="transform opacity-100 scale-100"
                leaveTo="transform opacity-0 scale-95"
              >
                <Menu.Items className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none z-10">
                  <div className="py-1">
                    {/* 編輯留言 - 需要權限 */}
                    {canEdit && (
                      <Menu.Item>
                        {({ active }) => (
                          <button
                            onClick={() => setIsEditing(true)}
                            className={`${
                              active ? 'bg-gray-100' : ''
                            } flex items-center px-4 py-2 text-sm text-gray-700 w-full text-left`}
                            disabled={loading}
                          >
                            <PencilIcon className="w-4 h-4 mr-2" />
                            編輯
                          </button>
                        )}
                      </Menu.Item>
                    )}
                    
                    {/* 刪除留言 - 需要權限 */}
                    {canDelete && (
                      <Menu.Item>
                        {({ active }) => (
                          <button
                            onClick={handleDelete}
                            className={`${
                              active ? 'bg-gray-100' : ''
                            } flex items-center px-4 py-2 text-sm text-red-700 w-full text-left`}
                            disabled={loading}
                          >
                            <TrashIcon className="w-4 h-4 mr-2" />
                            刪除
                          </button>
                        )}
                      </Menu.Item>
                    )}
                  </div>
                </Menu.Items>
              </Transition>
            </Menu>
          )}
        </div>

        {/* 留言正文 */}
        <div className="mt-2">
          {isEditing ? (
            <div className="space-y-3">
              <OfficialFroalaEditor
                value={editContent}
                onChange={setEditContent}
                placeholder="編輯留言內容..."
                autoFocus
                height={200}
                commentId={comment.id}
              />
              <div className="flex justify-end space-x-2">
                <button
                  onClick={handleCancel}
                  className="px-3 py-1 text-sm text-gray-600 hover:text-gray-800"
                  disabled={loading}
                >
                  取消
                </button>
                <button
                  onClick={handleSave}
                  className="px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
                  disabled={loading || editContent.trim() === ''}
                >
                  {loading ? '儲存中...' : '儲存'}
                </button>
              </div>
            </div>
          ) : (
            <div className="prose prose-sm max-w-none text-gray-700">
              <div 
                ref={commentContentRef}
                dangerouslySetInnerHTML={{ __html: comment.content }} 
              />
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

const CommentSection = ({ 
  comments, 
  issueId,
  currentUser, 
  onAddComment, 
  onUpdateComment, 
  onDeleteComment,
  canCreateComment = true,
  canEditComment = true,
  canDeleteComment = true
}: CommentSectionProps) => {
  const [newComment, setNewComment] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [showEditor, setShowEditor] = useState(false)

  const handleSubmit = async () => {
    if (newComment.trim() === '' || !canCreateComment) return

    setIsSubmitting(true)
    try {
      await onAddComment(newComment)
      setNewComment('')
      setShowEditor(false) // 送出後關閉編輯器
    } catch (error) {
      console.error('新增留言失敗:', error)
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleCancel = () => {
    setNewComment('')
    setShowEditor(false)
  }


  return (
    <div className="space-y-6">
      {/* 留言標題 */}
      <div className="border-b border-gray-200 pb-4">
        <h3 className="text-lg font-medium text-gray-900">
          留言 ({comments.length})
        </h3>
      </div>

      {/* 新增留言 */}
      {canCreateComment ? (
        <div className="space-y-4">
          {!showEditor ? (
            // 示意框模式 - 兩行高度
            <div 
              className="flex space-x-3 cursor-pointer"
              onClick={() => setShowEditor(true)}
            >
              <div className="flex-shrink-0">
                {currentUser.avatar ? (
                  <img
                    src={currentUser.avatar}
                    alt={currentUser.name}
                    className="w-8 h-8 rounded-full"
                  />
                ) : (
                  <div className="w-8 h-8 rounded-full bg-gray-300 flex items-center justify-center">
                    <span className="text-xs font-medium text-gray-600">
                      {currentUser.name.charAt(0).toUpperCase()}
                    </span>
                  </div>
                )}
              </div>
              <div className="flex-1">
                <div className="border border-gray-200 rounded-md px-3 py-2 hover:border-gray-300 transition-colors bg-gray-50 hover:bg-white">
                  <div className="text-gray-500 text-sm leading-5">
                    <div className="h-5">點擊新增留言...</div>
                    <div className="h-5 opacity-0">空行</div>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            // 完整編輯器模式
            <>
              <div className="flex space-x-3">
                <div className="flex-shrink-0">
                  {currentUser.avatar ? (
                    <img
                      src={currentUser.avatar}
                      alt={currentUser.name}
                      className="w-8 h-8 rounded-full"
                    />
                  ) : (
                    <div className="w-8 h-8 rounded-full bg-gray-300 flex items-center justify-center">
                      <span className="text-xs font-medium text-gray-600">
                        {currentUser.name.charAt(0).toUpperCase()}
                      </span>
                    </div>
                  )}
                </div>
                <div className="flex-1">
                  <OfficialFroalaEditor
                    value={newComment}
                    onChange={setNewComment}
                    placeholder="新增留言..."
                    height={150}
                    autoFocus
                    issueId={issueId}
                  />
                </div>
              </div>
              
              <div className="flex justify-end space-x-2">
                <button
                  onClick={handleCancel}
                  className="px-3 py-1 text-sm text-gray-600 hover:text-gray-800"
                  disabled={isSubmitting}
                >
                  取消
                </button>
                <button
                  onClick={handleSubmit}
                  className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
                  disabled={isSubmitting || newComment.trim() === ''}
                >
                  <PaperAirplaneIcon className="w-4 h-4" />
                  <span>{isSubmitting ? '送出中...' : '送出留言'}</span>
                </button>
              </div>
            </>
          )}
        </div>
      ) : (
        <div className="text-center py-4 text-gray-500">
          <p>您沒有新增留言的權限</p>
        </div>
      )}

      {/* 留言列表 */}
      <div className="space-y-1">
        {comments.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <UserCircleIcon className="w-12 h-12 mx-auto mb-4 text-gray-300" />
            <p>尚無留言</p>
            <p className="text-sm">成為第一個留言的人吧！</p>
          </div>
        ) : (
          comments.map((comment) => (
            <CommentItem
              key={comment.id}
              comment={comment}
              currentUser={currentUser}
              onUpdate={(content) => onUpdateComment(comment.id, content)}
              onDelete={() => onDeleteComment(comment.id)}
              canEdit={canEditComment}
              canDelete={canDeleteComment}
            />
          ))
        )}
      </div>
    </div>
  )
}

export default CommentSection
